import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  model,
  OnInit,
  signal,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  ButtonComponent,
  ButtonOptions,
  DateComponent,
  DateOptions,
  FormComponent,
  ModalComponent,
  ModalOptions
} from '@shared/components';
import {Events} from '@core/interfaces';
import {APIService, DataService, ToastService} from '@core/services';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormGroupDirective,
  NgForm,
  ReactiveFormsModule, ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {HTTPMethods, ModalTypes} from '@core/enums';
import {assets} from '@core/constants';
import {DropdownComponent, DropdownOptions} from '@shared/components/dropdown/dropdown.component';
import {DefaultSearchComponent} from '@shared/components/dropdown/search/default-search/default-search.component';
import {HttpHeaders} from '@angular/common/http';
import {PatientService} from '@core/services/patient.service';
import {Response, ResponseSingle} from '@core/classes/other/response';
import {Coding} from '@core/classes/other/coding';
import {lastValueFrom} from 'rxjs';
import {ATC} from '@core/classes/other/atc';
import {NgOptimizedImage} from '@angular/common';
import {Allergy} from '@core/classes/tables/allergy';
import {checkDate} from '@core/helpers';
import moment from 'moment/moment';

@Component({
  selector: 'ncp-add-allergy',
  imports: [
    ButtonComponent,
    ModalComponent,
    DropdownComponent,
    DateComponent,
    ReactiveFormsModule,
    NgOptimizedImage
  ],
  templateUrl: './add-allergy.component.html',
  styleUrl: './add-allergy.component.scss',
})
export class AddAllergyComponent implements OnInit, AfterViewInit {
  @ViewChild('mod') public mod!: ModalComponent;
  @ViewChild('aFrm') protected allergiesFormEl?: NgForm;
  @ViewChild('addDrugAllergyButton') public addDrugAllergyButton!: ButtonComponent;
  @ViewChild('addNonDrugAllergyButton') public addNonDrugAllergyButton!: ButtonComponent;

  @ViewChild( 'allergyDesc') public allergyDesc!: DropdownComponent<Coding>;
  @ViewChild( 'agentDesc') public agentDesc!: DropdownComponent<Coding>;
  @ViewChild( 'reactionDesc') public reactionDesc!: DropdownComponent<Coding>;
  @ViewChild( 'severityDesc') public severityDesc!: DropdownComponent<Coding>;
  @ViewChild('onSetDate') public onSetDate!: DateComponent;

  @ViewChild('allergenTemplate') public allergenTemplate!: TemplateRef<ATC>;

  @ViewChild('addDrugAllergyButton') getDAbtn  = () => {
    return this.addDrugAllergyButton
  }

  protected readonly toastService = inject(ToastService);
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  private apiService = inject(APIService);
  private patientService = inject(PatientService);
  private dataService = inject(DataService);

  protected data = model<any>();

  private _params = {};
  protected form: FormGroup = new FormGroup({
    allergy_coding_id: new FormControl<number | string | undefined>(undefined, [Validators.required]),
    agent_coding_id: new FormControl<number | string | undefined>(undefined, []),
    allergen_coding_id: new FormControl<number | string | undefined>(undefined, []),
    reaction_coding_id: new FormControl<number | string | undefined>(undefined, []),
    severity_coding_id: new FormControl<number | string | undefined>(undefined, []),
    allergy_onset_date: new FormControl<string>('', [Validators.required]),
  }, {
    validators: [this.formValidator()]
  });

  formValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let errors: ValidationErrors | null = {};

      let allergyCodingId = (control as FormGroup).controls['allergy_coding_id'];
      let agentCodingId = (control as FormGroup).controls['agent_coding_id'];
      let allergenCodingId = (control as FormGroup).controls['allergen_coding_id'];
      let onSetDate = (control as FormGroup).controls['allergy_onset_date'];

      let patientDOB = moment(this.patientService.data?.dob, ['YYYY-MM-DD', 'YYYY/MM/DD', 'YYYY.MM.DD', 'DD-MM-YYYY', 'DD/MM/YYYY', 'DD.MM.YYYY'], true);
      let today = moment();

      if(allergyCodingId.value === undefined || allergyCodingId.value === null) {
        allergyCodingId.setErrors({da_allergyCodingIdInvalid: true});
        errors['da_allergyCodingIdInvalid'] = true;
      }

      if(agentCodingId.hasValidator(Validators.required) && (agentCodingId.value === undefined || agentCodingId.value === null)) {
        agentCodingId.setErrors({da_agentCodingIdInvalid: true});
        errors['da_agentCodingIdInvalid'] = true;
      }

      if(allergenCodingId.hasValidator(Validators.required) && (allergenCodingId.value === undefined || allergenCodingId.value === null)) {
        allergenCodingId.setErrors({da_allergenCodingIdInvalid: true});
        errors['nda_allergenCodingIdInvalid'] = true;
      }

      if((!checkDate(onSetDate.value).isValid())) {
        onSetDate.setErrors({da_onSetDateInvalid: true});
        errors['da_onSetDateInvalid'] = true;
      } else {
        if(checkDate(onSetDate.value).isBefore(patientDOB)) {
          onSetDate.setErrors({da_onSetDateAfterDOB: true});
          errors['da_onSetDateAfterDOB'] = true;
        } else if(checkDate(onSetDate.value).isSameOrAfter(today)) {
          onSetDate.setErrors({da_onSetDateBeforeToday: true});
          errors['da_onSetDateBeforeToday'] = true;
        } else {
          onSetDate.setErrors(null);
        }
      }

      return Object.keys(errors).length > 0 ? errors : null;
    }
  }

  public btnNDAOptions = signal<ButtonOptions>(
    new ButtonOptions({
      id: 'ndaBtn',
      tooltip: 'add_non_drug_allergy',
    })
  );

  public btnDAOptions = signal<ButtonOptions>(
    new ButtonOptions({
      id: 'daBtn',
      tooltip: 'add_drug_allergy',
    })
  );

  protected modOptions = model<ModalOptions>(
    new ModalOptions({
      id: 'modal',
      type: ModalTypes.form,
      form: FormComponent,
      options: {
        animation: true,
        size: 'xl',
        fullscreen: "lg",
        backdrop: 'static',
        keyboard: false,
      },
      onClose: () => {this.onFormReset()}
    })
  );

  protected allergyDescDDOptions = signal<DropdownOptions<Coding>>(
    new DropdownOptions<Coding>({
      id: 'allergyDescription',
      name: 'allergyDescription',
      placeholder: 'allergy_description',
      label: {
        id: "allergy_desc_label",
        text: 'allergy_description',
      },
      api: {
        path: '/get-all_codings',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        body: {
          oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.18',
          description: ''
        },
      },
      required: true,
      searchComponent: DefaultSearchComponent,
      formControl: this.form.get('allergy_coding_id') as FormControl
    })
  );

  protected agentDescDDOptions = signal<DropdownOptions<Coding | ATC>>(
    new DropdownOptions<Coding | ATC>({
      id: 'agentDescription',
      name: 'agentDescription',
      placeholder: 'agent_description',
      label: {
        id: "agent_desc_label",
        text: 'agent_description',
      },
      api: {
        path: '/get-all_codings',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        body: {
          oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.19',
          description: ''
        },
      },
      required: true,
      searchComponent: DefaultSearchComponent,
    })
  );

  protected reactionDescDDOptions = signal<DropdownOptions<Coding>>(
    new DropdownOptions<Coding>({
      id: 'reactionDescription',
      name: 'reactionDescription',
      placeholder: 'reaction_description',
      label: {
        id: "reaction_desc_label",
        text: 'reaction_description',
      },
      api: {
        path: '/get-all_codings',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        body: {
          oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.11',
          description: ''
        },
      },
      searchComponent: DefaultSearchComponent,
      formControl: this.form.get('reaction_coding_id') as FormControl
    })
  );

  protected severityDescDDOptions = signal<DropdownOptions<Coding>>(
    new DropdownOptions<Coding>({
      id: 'severityDescription',
      name: 'severityDescription',
      placeholder: 'severity_description',
      label: {
        id: "severity_desc_label",
        text: 'severity_description',
      },
      api: {
        path: '/get-all_codings',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        body: {
          oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.13',
          description: ''
        },
      },
      searchComponent: DefaultSearchComponent,
      formControl: this.form.get('severity_coding_id') as FormControl
    })
  );

  protected onSetDateOptions = signal<DateOptions>(
    new DateOptions({
      id: 'onSetDate',
      label: {
        text: 'on_set',
      },
      name: 'onSetDate',
      initialValue: '',
      formControl: this.form.get('allergy_onset_date') as FormControl,
      required: true,
      minDate: this.patientService.patient?.data?.dob,
      maxDate: moment().format('YYYY-MM-DD'),
    })
  );

  ngOnInit() {
    this.btnNDAOptions().onClick = (event) => {
      (this.form.get('agent_coding_id') as FormControl).setValidators([Validators.required]);
      (this.form.get('allergen_coding_id') as FormControl).clearValidators();
      (this.form.get('allergen_coding_id') as FormControl).setErrors([]);
      (this.form.get('allergen_coding_id') as FormControl).updateValueAndValidity();
      //this.onFormReset();
      this.mod.options().title = 'add_non_drug_allergy';
      this.mod.options().onReset = this.onFormReset;
      this.allergyDescDDOptions().label!.text = 'allergy_description';
      let agentOptions = this.agentDescDDOptions();
      agentOptions.label!.text = 'agent_description';
      agentOptions.id = 'agentDescription';
      agentOptions.name = 'agentDescription';
      agentOptions.itemTemplate = undefined;
      agentOptions.formControl = this.form.get('agent_coding_id') as FormControl;
      agentOptions.api = {
        path: '/get-all_codings',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        body: {
          oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.19',
          description: ''
        },
      }
      this.allergyDescDDOptions().placeholder = 'allergy_description';
      agentOptions.placeholder = 'agent_description';
      this.agentDesc.name = 'agentDescription';
      this.agentDescDDOptions.set(agentOptions);
      this.agentDesc.detectChanges();
      this.addNonDrugAllergyButton.blur();
      this.btnNDAOptions().isLoading = true;
      //this.formValidator();
      this.downloadDropDownData(this.mod.options().title).then((value) => {
        this.btnNDAOptions().isLoading = false;
        this.changeDetectorRef.detectChanges();
        this.mod.open();
      });
    };

    this.btnDAOptions().onClick = (event) => {
      (this.form.get('agent_coding_id') as FormControl).clearValidators();
      (this.form.get('agent_coding_id') as FormControl).setErrors([]);
      (this.form.get('agent_coding_id') as FormControl).updateValueAndValidity();
      (this.form.get('allergen_coding_id') as FormControl).setValidators([Validators.required]);

      this.mod.options().title = 'add_drug_allergy';
      this.mod.options().onReset = this.onFormReset;
      this.allergyDescDDOptions().label!.text = 'drug_allergy_description';
      let agentOptions = this.agentDescDDOptions();
      agentOptions.label!.text = 'allergen_description';
      agentOptions.id = 'allergenDescription';
      agentOptions.name = 'allergenDescription';
      agentOptions.itemTemplate = this.allergenTemplate;
      agentOptions.formControl = this.form.get('allergen_coding_id') as FormControl;
      agentOptions.api = {
        path: '/get-atc',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        body: {
          description: ''
        },
      }
      this.allergyDescDDOptions().placeholder = 'drug_allergy_description';
      agentOptions.placeholder = 'allergen_description';
      this.agentDesc.name = 'allergenDescription';
      this.agentDescDDOptions.set(agentOptions);
      this.agentDesc.detectChanges();
      this.addDrugAllergyButton.blur();
      this.btnDAOptions().isLoading = true;
      this.downloadDropDownData(this.mod.options().title).then((value) => {
        this.btnDAOptions().isLoading = false;
        this.changeDetectorRef.detectChanges();
        this.mod.open();
      });
    };

    this.modOptions().onSave = this.onSave;
  }

  onFormReset = () => {
    console.log('on reset form');
    this.form.reset();
    this.allergyDesc.clearSelection();
    this.agentDesc.clearSelection();
    this.reactionDesc.clearSelection();
    this.severityDesc.clearSelection();
    this.onSetDate.reset();

    this.modOptions().errors = [];
  }

  ngAfterViewInit(): void {

  }

  protected downloadDropDownData = async (title?: string) => {
    let ddData = JSON.parse(localStorage.getItem('dropDownData')!) as Object || {};

    if(ddData.hasOwnProperty('allergyDescription')
      && ((title === 'add_non_drug_allergy') ? ddData.hasOwnProperty('agentDescription') : ddData.hasOwnProperty('allergenDescription'))
      && ddData.hasOwnProperty('reactionDescription')
      && ddData.hasOwnProperty('severityDescription')) {
      return;
    }

    const data = await lastValueFrom(this.apiService.callMany([
      ...title === 'add_non_drug_allergy' && !ddData.hasOwnProperty('allergyDescription') ? [{
        path: '/get-all_codings',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        body: {
          oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.18',
          description: ''
        },
        name: 'allergyDescription'
      }] : [],
      ...title === 'add_non_drug_allergy' && !ddData.hasOwnProperty('agentDescription') ? [{
        path: '/get-all_codings',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        body: {
          oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.19',
          description: ''
        },
        name: 'agentDescription'
      }] : [],
      ...title === 'add_drug_allergy' && !ddData.hasOwnProperty('allergenDescription') ? [{
        path: '/get-all_atc',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        body: {
          description: ''
        },
        name: 'allergenDescription'
      }] : [],
      ...title === 'add_non_drug_allergy' && !ddData.hasOwnProperty('reactionDescription') ? [{
        path: '/get-all_codings',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        body: {
          oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.11',
          description: ''
        },
        name: 'reactionDescription'
      }] : [],
      ...title === 'add_non_drug_allergy' && !ddData.hasOwnProperty('severityDescription') ? [{
        path: '/get-all_codings',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        body: {
          oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.13',
          description: ''
        },
        name: 'severityDescription'
      }] : []
    ])!);

    if (data) {
      if(data['allergyDescription']) {
        Object.defineProperty(ddData, 'allergyDescription', {
          value: (data['allergyDescription'] as Response<Coding>).data,
          enumerable: true,
          writable: false
        });
        this.allergyDesc.data = (data['allergyDescription'] as Response<Coding>).data || [];
      }
      if(data['agentDescription']) {
        Object.defineProperty(ddData, 'agentDescription', {
          value: (data['agentDescription'] as Response<Coding>).data,
          enumerable: true,
          writable: false
        });
        this.agentDesc.data = (data['agentDescription'] as Response<Coding>).data || [];
      }
      if(data['allergenDescription']) {
        Object.defineProperty(ddData, 'allergenDescription', {
          value: (data['allergenDescription'] as Response<Coding>).data,
          enumerable: true,
          writable: false
        });
        this.agentDesc.data = (data['allergenDescription'] as Response<Coding>).data || [];
      }
      if (data['reactionDescription']) {
        Object.defineProperty(ddData, 'reactionDescription', {
          value: (data['reactionDescription'] as Response<Coding>).data,
          enumerable: true,
          writable: false
        });
        this.reactionDesc.data = (data['reactionDescription'] as Response<Coding>).data || [];
      }
      if (data['severityDescription']) {
        Object.defineProperty(ddData, 'severityDescription', {
          value: (data['severityDescription'] as Response<Coding>).data,
          enumerable: true,
          writable: false
        });
        this.severityDesc.data = (data['severityDescription'] as Response<Coding>).data || [];
      }
      localStorage.setItem('dropDownData', JSON.stringify(ddData));
    }
  }

  onSave = () => {
    this.allergiesFormEl?.ngSubmit.emit();
  }

  validate = (): boolean => {
    Object.keys(this.form.controls).forEach((control) => {
      this.form.controls[control].markAsTouched();
    })

    return true;
  }

  onFormSubmit = () => {
    console.log(this.form.value);
    if(this.validate() && this.form.invalid) {
      console.log('this.form.is.invalid');
      this.modOptions().errors = this.form.errors!;
      this.mod.detectChanges();
      return;
    }

    this.modOptions().disableOnSave = true;
    let body = {
      allergy_onset_date: (this.form.get('allergy_onset_date') as FormControl).value,
      allergy_coding_id: this.allergyDescDDOptions().value!.coding_id,
      agent_coding_id: this.agentDescDDOptions().value!.coding_id,
      reaction_coding_id: this.reactionDescDDOptions().value?.coding_id,
      severity_coding_id: this.severityDescDDOptions().value?.coding_id,
      status: 1,
      version_id: this.patientService.version!.new,
    }

    this.apiService.call({
      path: '/allergies',
      headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
      type: HTTPMethods.POST,
      body: body
    })?.subscribe((res: ResponseSingle<Allergy>) => {
      if(res && res.status?.toString() === '1') {
        let data = this.data()?.tblOptions().data
        data.unshift(res.data);
        if (this.data && this.data() && this.data().tbl && this.data().tbl.tableData) {
          this.data()!.tbl!.tableData! = data;
        }
        this.mod.close();
      }
    })
  }

  protected readonly assets = assets;
}
